"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { FormStatus } from "@/components/ui/Form";
import {
  fetchGalleryImages,
  uploadGalleryImages,
  deleteGalleryImage,
  setGalleryMainImage,
  type UserGalleryImage,
} from "@/lib/api/c-photo-gallery-user";
import { confirmDialog } from "@/lib/notifications/confirmDialog";
import { FILETYPE_IMAGE, validateFiles } from "@/lib/utils/fileValidation";
import styles from "./CPhotoGallery.module.css";

/**
 * Zarzadzanie zdjęciami galerii usera: upload (multipart `images[]`), siatka
 * miniatur z akcjami usun / ustaw jako okładkę (is_main).
 *
 * Upload idzie przez backend `insertFiles` (te same miniatury s_/ss_ co panel
 * admina) - reuse, bez budowania nowego uploadera.
 */
export function GalleryImages({
  galleryId,
  onBack,
}: {
  galleryId: number;
  onBack: () => void;
}) {
  const [images, setImages] = useState<UserGalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { images: rows } = await fetchGalleryImages(galleryId);
      setImages(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [galleryId]);

  useEffect(() => { void reload(); }, [reload]);

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(null);
    setInfo(null);

    const { accepted, rejected } = validateFiles(files, FILETYPE_IMAGE);
    if (accepted.length === 0) {
      setError(rejected.map((r) => r.reason).join("\n"));
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    setUploading(true);
    try {
      const { uploaded, errors } = await uploadGalleryImages(galleryId, accepted);
      const allErrors = [...rejected.map((r) => r.reason), ...errors];
      if (allErrors.length > 0) setError(allErrors.join("\n"));
      if (uploaded > 0) setInfo(`Wgrano zdjęć: ${uploaded}.`);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (img: UserGalleryImage) => {
    const ok = await confirmDialog({
      title: "Usunąć zdjęcie?",
      message: "Zdjęcie zostanie trwale usunięte z galerii.",
      confirmLabel: "Usuń",
      cancelLabel: "Anuluj",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await deleteGalleryImage(galleryId, img.id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleSetMain = async (img: UserGalleryImage) => {
    try {
      await setGalleryMainImage(galleryId, img.id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className={styles.images}>
      <div className={styles.imagesToolbar}>
        <button type="button" className={styles.btnSecondary} onClick={onBack}>
          ← Wróć do listy
        </button>
        <label className={styles.btnPrimary}>
          {uploading ? "Wgrywanie..." : "+ Dodaj zdjęcia"}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            hidden
            disabled={uploading}
            onChange={(e) => handleUpload(e.target.files)}
          />
        </label>
      </div>

      {error && <FormStatus variant="error">{error}</FormStatus>}
      {info && <FormStatus variant="success">{info}</FormStatus>}

      {loading ? (
        <p className={styles.empty}>Wczytywanie...</p>
      ) : images.length === 0 ? (
        <p className={styles.empty}>Ta galeria nie ma jeszcze zdjęć. Dodaj pierwsze!</p>
      ) : (
        <ul className={styles.imageGrid}>
          {images.map((img) => (
            <li key={img.id} className={img.is_main ? styles.imageItemMain : styles.imageItem}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={img.thumb_src} alt={img.description || ""} loading="lazy" decoding="async" />
              {img.is_main && <span className={styles.mainBadge}>Okładka</span>}
              <div className={styles.imageActions}>
                {!img.is_main && (
                  <button type="button" className={styles.btnLink} onClick={() => handleSetMain(img)}>
                    Ustaw jako okładkę
                  </button>
                )}
                <button type="button" className={styles.btnDanger} onClick={() => handleDelete(img)}>
                  Usuń
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
